import { AnimatePresence, motion } from "framer-motion";
import { Check, Luggage, Users } from "lucide-react";
import { formatChf } from "@/lib/data";
import { useLang } from "@/lib/i18n";

export default function VehicleDetailPanel({ vehicle, price, minutes }) {
  const { t } = useLang();
  const i18nVeh = t(`veh.${vehicle.id}`);
  const features = i18nVeh.features || [];

  return (
    <AnimatePresence mode="wait">
      <motion.article
        key={vehicle.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
        className="overflow-hidden rounded-[1.5rem] border border-ink/10 bg-white shadow-[0_24px_70px_-50px_rgba(16,24,40,0.32)]"
        data-testid="vehicle-detail-panel"
      >
        <div className="relative h-64 bg-gradient-to-br from-white via-miles-ice/70 to-cloud sm:h-80">
          <div className="absolute bottom-8 left-1/2 h-16 w-[62%] -translate-x-1/2 rounded-[100%] bg-miles-deep/10 blur-xl" />
          <img src={vehicle.image} alt={i18nVeh.model} className="img-clean relative h-full w-full object-contain p-6 sm:p-8" />
        </div>
        <div className="p-6 sm:p-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-sm text-slate">{i18nVeh.model}</p>
              <h2 className="mt-1 text-3xl font-semibold tracking-[-0.04em] text-ink">{i18nVeh.name}</h2>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate">{t("bk.fixed_price")}</p>
              <p className="mt-1 text-2xl font-semibold tracking-tight text-ink">{formatChf(price)}</p>
            </div>
          </div>
          <p className="mt-5 max-w-2xl text-lg font-light leading-relaxed text-miles-deep/85">{i18nVeh.editorial}</p>
          <div className="mt-6 grid gap-4 border-t border-ink/10 pt-6 sm:grid-cols-3">
            {features.map((feature) => (
              <p key={feature} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-miles" strokeWidth={2} /> {feature}
              </p>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-ink/10 pt-5 text-sm font-medium text-ink">
            <span className="flex items-center gap-2"><Users className="h-4 w-4 text-miles" strokeWidth={1.5} /> {t("veh.pax").replace("{n}", vehicle.pax)}</span>
            <span className="flex items-center gap-2"><Luggage className="h-4 w-4 text-miles" strokeWidth={1.5} /> {t("veh.bags").replace("{n}", vehicle.bags)}</span>
            {minutes != null && <span className="text-slate">{t("bk.vehicle_route").replace("{min}", minutes)}</span>}
          </div>
        </div>
      </motion.article>
    </AnimatePresence>
  );
}
